import { DatePicker } from '@/components';
import {
	dayjs,
	formatDate,
	getDisabledTime,
	insertObjectIfV2,
	SUPPORT_FORMAT_DATE_LIST,
} from '@/utils';
import React, { memo, useMemo } from 'react';

import type { Dayjs } from 'dayjs';
import { Field, FieldProps } from 'formik';

import { IDatePickerProps } from '../date-picker';

interface IFDateRangePickerProps extends IDatePickerProps {
	format?: (typeof SUPPORT_FORMAT_DATE_LIST)[number];
	values?: any;
	startDateKey?: string;
	endDateKey?: string;
	disabledPastTime?: boolean;
	startSameEndDate?: boolean;
}

const DateRangePicker: React.FC<IFDateRangePickerProps> = ({
	name,
	values,
	format,
	disabledPastTime,
	startDateKey = '',
	endDateKey = '',
	formatToUpdate = 'YYYY-MM-DD',
	startSameEndDate = false,
	...rest
}) => {
	const disabledRangeTimeFrom = useMemo(() => {
		return (current: Dayjs) =>
			getDisabledTime(
				current,
				dayjs(values?.[endDateKey], SUPPORT_FORMAT_DATE_LIST),
				true
			);
	}, [endDateKey]);

	const disabledRangeTimeTo = useMemo(() => {
		return (current: Dayjs) =>
			getDisabledTime(
				current,
				dayjs(values?.[startDateKey], SUPPORT_FORMAT_DATE_LIST),
				false
			);
	}, [startDateKey]);

	return (
		<Field name={`${startDateKey}-${endDateKey}`}>
			{({
				field,
				form: { setFieldValue, errors, touched, setValues },
				meta,
			}: FieldProps) => {
				return (
					<div className="flex items-center justify-center">
						<DatePicker
							{...rest}
							key={startDateKey}
							id={startDateKey}
							name={startDateKey}
							format={format}
							value={values?.[startDateKey]}
							onChange={(date, dateString) => {
								const dateFormatted = formatDate(date, {
									outputFormat: formatToUpdate,
								});

								setValues((prevValues: any) => ({
									...prevValues,
									[startDateKey]: dateFormatted,
									...(startSameEndDate ? { [endDateKey]: dateFormatted } : {}),
								}));
							}}
							{...insertObjectIfV2(disabledPastTime, {
								minDate: dayjs(),
							})}
							{...insertObjectIfV2(values?.[endDateKey] && !startSameEndDate, {
								maxDate: dayjs(values?.[endDateKey], SUPPORT_FORMAT_DATE_LIST),
								disabledTime: disabledRangeTimeFrom,
							})}
							allowClear
							touched={!!touched?.[startDateKey]}
							errorCode={errors?.[startDateKey] as string}
						/>
						<span className="px-2">-</span>
						<DatePicker
							{...rest}
							key={endDateKey}
							id={endDateKey}
							name={endDateKey}
							format={format}
							value={values?.[endDateKey]}
							onChange={(date, dateString) => {
								const dateFormatted = formatDate(date, {
									outputFormat: formatToUpdate,
								});

								setValues((prevValues: any) => ({
									...prevValues,
									[endDateKey]: dateFormatted,
									...(startSameEndDate
										? { [startDateKey]: dateFormatted }
										: {}),
								}));
							}}
							{...insertObjectIfV2(disabledPastTime, {
								minDate: dayjs(),
							})}
							{...insertObjectIfV2(
								values?.[startDateKey] && !startSameEndDate,
								{
									minDate: dayjs(
										disabledPastTime
											? dayjs(
													values?.[startDateKey],
													SUPPORT_FORMAT_DATE_LIST
												).isAfter(dayjs())
												? values?.[startDateKey]
												: dayjs()
											: values?.[startDateKey],
										SUPPORT_FORMAT_DATE_LIST
									),
									disabledTime: disabledRangeTimeTo,
								}
							)}
							allowClear
							touched={!!touched?.[endDateKey]}
							errorCode={errors?.[endDateKey] as string}
						/>
					</div>
				);
			}}
		</Field>
	);
};

export default memo(DateRangePicker);
